import { t } from '@lingui/core/macro';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/common/components/ui/tooltip';
import { formatDateTime } from '@/common/utils/formatDateTime';
import { statusColor } from '@/features/telemetry/utils/statusColor';
import type { StatusSegment } from '@/features/telemetry/mappers/toStatusSegments';

export interface StatusTimelineBarProps {
  label: string;
  segments: StatusSegment[];
  from: Date;
  to: Date;
}

function segmentTooltipLabel(segment: StatusSegment): string {
  return `${formatDateTime(segment.from, 'dateTimeCompact')} – ${formatDateTime(segment.to, 'timeShort')}`;
}

const GAP_STRIPE_BACKGROUND =
  'repeating-linear-gradient(135deg, var(--muted), var(--muted) 4px, transparent 4px, transparent 8px)';

/**
 * One channel's timeline: a segmented bar whose widths are proportional to how long
 * each raw status value held, coloured by a deterministic (non-semantic) hash of that
 * value. A `null` segment — a genuine reporting gap — renders muted with no label.
 */
export function StatusTimelineBar({ label, segments, from, to }: StatusTimelineBarProps) {
  const totalMs = to.getTime() - from.getTime();
  if (totalMs <= 0) return null;

  return (
    <div>
      <p className="mb-1 text-[11px] text-muted-foreground">{label}</p>
      <div className="flex h-5 overflow-hidden rounded-full">
        {segments.map((segment, index) => {
          const widthPct = ((segment.to.getTime() - segment.from.getTime()) / totalMs) * 100;

          return (
            <Tooltip key={`${segment.from.getTime()}-${index}`}>
              <TooltipTrigger asChild>
                <div
                  data-testid="status-segment"
                  // Not `data-status` — TanStack Router's own `Link` already uses that
                  // attribute globally (active/inactive), which collides with any
                  // DOM query scoped to this component's ancestor tree.
                  data-segment-status={segment.status ?? undefined}
                  // Focusable so the tooltip (Radix shows it on focus as well as
                  // hover) reaches keyboard users too, with an aria-label carrying
                  // the same status + time-range info as the tooltip content, since
                  // a gap segment has no visible text of its own to name it by.
                  tabIndex={0}
                  role="img"
                  aria-label={`${segment.status ?? t`No data`} · ${segmentTooltipLabel(segment)}`}
                  className="flex h-full shrink-0 cursor-default items-center overflow-hidden px-1.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                  style={{
                    width: `${widthPct}%`,
                    // Painted as a background on this same padded box — same as the
                    // solid colour below — rather than an inset child div, which
                    // used to leave the stripe pattern short of the segment's actual
                    // edges (and so, at the ends of the bar, short of the parent's
                    // rounded clip too).
                    ...(segment.status === null
                      ? { backgroundImage: GAP_STRIPE_BACKGROUND }
                      : { backgroundColor: statusColor(segment.status) }),
                  }}
                >
                  {segment.status !== null && (
                    <span className="pointer-events-none truncate text-[10px] font-medium leading-none text-white select-none">
                      {segment.status}
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div className="font-medium">{segment.status ?? t`No data`}</div>
                <div className="mt-0.5 tabular-nums opacity-80">{segmentTooltipLabel(segment)}</div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
